// src/pages/ProfilePage.jsx
//
// The account page. It replaces a clone-era placeholder ("track your conversation
// skills progress", "currently being developed"), which was also the page an App
// Store reviewer reaches first after signing in with the demo account.
//
// It serves the web and the iOS app. On iOS it is a tab, so it also carries the
// places the web reaches through its header and drawer, Log Out included, and it
// handles signed-out visitors itself rather than bouncing them to a web page.
//
// Account deletion lives here because App Store rule 5.1.1(v) requires an app that
// lets people create an account to let them delete it from inside the app. It
// calls the auth hub's requestAccountDeletion directly: the hub holds every app's
// per-user data precisely so that one deletion reaches all of it, with the hub's
// 30-day window to change your mind.
//
// Australian English throughout.

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  User, LogOut, Trash2, ShieldCheck, LifeBuoy, FileText, ChevronRight,
  RefreshCw, Sparkles, Target, Feather, BookOpen, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePremium } from '../context/PremiumContext';
import { authHubApp } from '../services/firebase';
import { revenueCatService } from '../services/revenueCatService';
import { showToast } from '../components/ui/toast';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const IS_NATIVE = Capacitor.isNativePlatform();
const HUB_REGION = 'australia-southeast1';

const LinkRow = ({ to, icon: Icon, label }) => (
  <li>
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-lg text-pearl hover:bg-gunmetal/40 hover:text-bright-gold transition-colors"
    >
      <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      <ChevronRight className="w-4 h-4 text-pearl/40" aria-hidden="true" />
    </Link>
  </li>
);

// Plain statement of what this account can open. Never a price, never a link to
// buy anything outside the store (App Store rule 3.1.1).
const accessLabel = ({ betaMode, hasForever, subscriptionTier }) => {
  if (subscriptionTier === 'admin') return 'Staff access: everything is included.';
  if (subscriptionTier === 'founding') return 'Founding member: everything is included.';
  if (subscriptionTier) return 'Dive Deep Dialogues membership: everything is included.';
  if (betaMode) return 'Everything is unlocked while the app is being tested.';
  if (hasForever) return 'The full course: yours for good.';
  return 'Free: modules 1 and 2, their tools, the assessment, the Lineage and more.';
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, redirectToAuth, logOut } = useAuth();
  const premium = usePremium();
  const [restoring, setRestoring] = useState(false);
  // idle | confirm | working | done | reauth | error
  const [deletion, setDeletion] = useState({ step: 'idle' });

  const unlocked = premium.betaMode || premium.hasForever || Boolean(premium.subscriptionTier);

  const handleLogOut = async () => {
    await logOut();
    navigate('/', { replace: true });
  };

  const handleRestore = async () => {
    setRestoring(true);
    const result = await revenueCatService.restorePurchases();
    setRestoring(false);
    if (result.success && result.unlocked) showToast('Restored. Welcome back.');
    else if (result.success) showToast('No previous purchase was found on this Apple Account.');
    else showToast(result.error || 'Nothing could be restored.');
  };

  const handleDelete = async () => {
    if (!authHubApp) {
      setDeletion({ step: 'error', message: 'Account deletion isn’t available in this build. Please contact support.' });
      return;
    }
    setDeletion({ step: 'working' });
    try {
      const request = httpsCallable(getFunctions(authHubApp, HUB_REGION), 'requestAccountDeletion');
      const { data } = await request({ reason: 'Requested from Profound Philosophy' });
      setDeletion({ step: 'done', scheduledDeletionAt: data?.scheduledDeletionAt || null });
    } catch (error) {
      // The hub only accepts a deletion within minutes of signing in. A fresh
      // sign-in through the hub satisfies it.
      if (error?.details?.reason === 'recent-login-required' || error?.code === 'functions/unauthenticated') {
        setDeletion({ step: 'reauth' });
      } else {
        console.error('[ProfilePage] Account deletion request failed:', error?.code || error);
        setDeletion({
          step: 'error',
          message: 'Your account couldn’t be scheduled for deletion just now. Please try again, or contact support.',
        });
      }
    }
  };

  const accessCard = (
    <Card className="p-6">
      <h2 className="text-lg font-semibold text-white mb-1">Your access</h2>
      <p className="text-pearl/80">{premium.loading ? 'Checking…' : accessLabel(premium)}</p>
      {(!premium.loading && !unlocked) || IS_NATIVE ? (
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          {!premium.loading && !unlocked && (
            <Button size="medium" onClick={() => window.dispatchEvent(new CustomEvent('pph:open-paywall'))}>
              Unlock the full course
            </Button>
          )}
          {IS_NATIVE && (
            <Button size="medium" variant="secondary" onClick={handleRestore} disabled={restoring}>
              <span className="inline-flex items-center gap-2">
                <RefreshCw className={`w-4 h-4 ${restoring ? 'animate-spin' : ''}`} aria-hidden="true" />
                {restoring ? 'Checking…' : 'Restore purchases'}
              </span>
            </Button>
          )}
        </div>
      ) : null}
    </Card>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-28 pb-16"
    >
      <div className="container mx-auto px-4 sm:px-6 max-w-2xl space-y-6">
        <h1 className="text-3xl sm:text-4xl font-bold text-white">Your Profile</h1>

        {authLoading ? (
          <Card className="p-6">
            <p className="flex items-center gap-3 text-pearl/80" role="status">
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Checking your account…
            </p>
          </Card>
        ) : user ? (
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-bright-gold/15 flex items-center justify-center shrink-0" aria-hidden="true">
                <User className="w-6 h-6 text-bright-gold" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-semibold text-white break-words">{user.displayName}</p>
                {user.email && <p className="text-sm text-pearl/70 break-all">{user.email}</p>}
              </div>
            </div>
            <p className="mt-4 text-sm text-pearl/70">
              Signed in with your Dive Deep Dialogues account. The same account works in every app in the collection.
            </p>
          </Card>
        ) : (
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-2">You’re not signed in</h2>
            <p className="text-pearl/80 mb-5">
              Your progress is kept on this device. A free Dive Deep Dialogues account saves it to the cloud,
              carries it between devices, and lets you talk to Sofia.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="medium" onClick={() => redirectToAuth('signin')}>Sign in</Button>
              <Button size="medium" variant="secondary" onClick={() => redirectToAuth('signup')}>
                Create a free account
              </Button>
            </div>
          </Card>
        )}

        {!authLoading && accessCard}

        {/* On iOS the drawer is gone, so the places it held live here. */}
        {IS_NATIVE && (
          <Card className="p-2">
            <h2 className="sr-only">Explore</h2>
            <ul>
              <LinkRow to="/assessment" icon={Target} label="Philosophical Profile Assessment" />
              <LinkRow to="/lineage" icon={Sparkles} label="The Lineage" />
              <LinkRow to="/journal" icon={Feather} label="Journal" />
              <LinkRow to="/resources" icon={BookOpen} label="Resources" />
            </ul>
          </Card>
        )}

        <Card className="p-2">
          <h2 className="sr-only">Your data and help</h2>
          <ul>
            <LinkRow to="/privacy" icon={ShieldCheck} label="Data & Privacy" />
            <LinkRow to="/support" icon={LifeBuoy} label="Support" />
            <LinkRow to="/terms" icon={FileText} label="Terms of Use" />
          </ul>
        </Card>

        {user && (
          <>
            <Card className="p-6">
              <Button size="medium" variant="secondary" className="w-full" onClick={handleLogOut}>
                <span className="inline-flex items-center gap-2">
                  <LogOut className="w-4 h-4" aria-hidden="true" /> Log out
                </span>
              </Button>
            </Card>

            <Card className="p-6 border-red-500/30">
              <h2 className="text-lg font-semibold text-red-200 mb-2 flex items-center gap-2">
                <Trash2 className="w-5 h-5" aria-hidden="true" /> Delete your account
              </h2>

              <div aria-live="polite">
                {deletion.step === 'idle' && (
                  <>
                    <p className="text-pearl/80">
                      This deletes your Dive Deep Dialogues account and everything held against it, in this app
                      and in every other app in the collection. You’ll have 30 days to change your mind: sign in
                      again before then and you can cancel it from your account. Deleting your account doesn’t refund
                      purchases; <Link to="/support" className="text-bright-gold underline">Support</Link> explains refunds.
                    </p>
                    <button
                      type="button"
                      onClick={() => setDeletion({ step: 'confirm' })}
                      className="mt-4 w-full sm:w-auto px-5 py-3 min-h-[44px] rounded-xl border border-red-400/50 text-red-200 font-semibold hover:bg-red-500/10 transition-colors"
                    >
                      Delete account
                    </button>
                  </>
                )}

                {deletion.step === 'confirm' && (
                  <>
                    <p className="font-semibold text-white">Delete your account?</p>
                    <p className="mt-1 text-pearl/80">
                      Your progress, saved work and conversations with Sofia will be scheduled for deletion.
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="px-5 py-3 min-h-[44px] rounded-xl bg-red-700 text-white font-semibold hover:bg-red-800 transition-colors"
                      >
                        Yes, delete my account
                      </button>
                      <Button size="medium" variant="secondary" onClick={() => setDeletion({ step: 'idle' })}>
                        Keep my account
                      </Button>
                    </div>
                  </>
                )}

                {deletion.step === 'working' && (
                  <p className="flex items-center gap-3 text-pearl/80" role="status">
                    <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Scheduling the deletion…
                  </p>
                )}

                {deletion.step === 'done' && (
                  <>
                    <p className="text-white">
                      Your account is scheduled for deletion
                      {formatDate(deletion.scheduledDeletionAt) ? ` on ${formatDate(deletion.scheduledDeletionAt)}` : ''}.
                      Sign in any time before then to cancel.
                    </p>
                    <Button size="medium" className="mt-4" onClick={handleLogOut}>Done</Button>
                  </>
                )}

                {deletion.step === 'reauth' && (
                  <>
                    <p className="text-pearl/80">
                      For your security, please sign in again, then delete your account.
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <Button size="medium" onClick={() => redirectToAuth('signin')}>Sign in again</Button>
                      <Button size="medium" variant="secondary" onClick={() => setDeletion({ step: 'idle' })}>
                        Cancel
                      </Button>
                    </div>
                  </>
                )}

                {deletion.step === 'error' && (
                  <>
                    <p className="text-red-200">{deletion.message}</p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <Button size="medium" onClick={() => setDeletion({ step: 'confirm' })}>Try again</Button>
                      <Button size="medium" variant="secondary" onClick={() => setDeletion({ step: 'idle' })}>
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default ProfilePage;
